/**
 * O Plano de Contas da aba Cadastros: um cartão por categoria, com as
 * subcategorias, a cor e as exclusões.
 *
 * Papel: reproduz a metade de cima de `#tab-cadastros` e `renderCadastros`
 * (l. 465–476, 5779–5874): o título, a contagem, "Nova Categoria" e "Exportar
 * XLSX"; e, em grade, cada categoria com o seletor de cor, o nome, "sistema" ou
 * o 🗑, o "+ Subcategoria" e a tabela das subcategorias com o ✕ só na que não é
 * de sistema nem está em uso.
 *
 * Como usar: o plano chega da API em ordem alfabética, já com as categorias de
 * sistema, as criadas e as usadas em lançamentos. Mudar a cor grava quando o
 * seletor fecha (o `change` do navegador), sem aviso; o 🗑 e o ✕ perguntam
 * antes, e a categoria em uso é recusada pela API com a frase da exceção 7.
 * Quem só tem `ver` não vê "Nova Categoria", o 🗑, o "+ Subcategoria" nem o ✕,
 * e o seletor de cor vira um quadrado da mesma medida.
 *
 * A cor da categoria é dado gravado no plano, e por isso entra pelo `value` do
 * seletor e pelo `style` do quadrado; a categoria sem cor mostra a cor padrão
 * de categoria nova.
 */
import { COR_PADRAO_DA_CATEGORIA, type Categoria } from '@concierge/shared';
import { IconPlus, IconTableExport } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';

import { useNotificacao } from '@/shared/embutido';
import {
  useExcluirCategoria,
  useExcluirSubcategoria,
  useMudarCorDaCategoria,
} from '../hooks/usar-financas';
import { Botao } from './kit/botao';
import { BotaoDeAcao } from './kit/botao-de-acao';
import { CabecalhoDoCartao, Cartao } from './kit/cartao';
import { ModalNovaCategoria } from './modal-nova-categoria';
import { ModalNovaSubcategoria } from './modal-nova-subcategoria';

/** O título de seção de Cadastros, em Outfit 15 px 300 caixa alta. */
export const CLASSE_DO_TITULO_DE_CADASTROS =
  'font-outfit text-[15px] font-light tracking-[0.08em] text-foreground uppercase';

interface PlanoDeContasProps {
  /** O plano; indefinido enquanto não chega. */
  plano: readonly Categoria[] | undefined;
  /** Verdadeiro quando a consulta do plano falhou. */
  falhou: boolean;
  onTentarDeNovo: () => void;
  podeEditar: boolean;
  /** O "Exportar XLSX" dos cadastros. */
  onExportar: () => void;
}

/**
 * O seletor de cor que grava só quando fecha.
 *
 * O React chama `onChange` a cada cor percorrida dentro do seletor; a origem
 * grava no `change` nativo, que o navegador só dispara ao fechar o seletor. Por
 * isso o campo não é controlado e ouve o `change` por conta própria.
 *
 * @param cor - A cor gravada.
 * @param nome - O nome da categoria, para o nome acessível.
 * @param onMudar - Recebe a cor escolhida, ao fechar.
 * @returns O seletor.
 */
function SeletorDeCor({
  cor,
  nome,
  onMudar,
}: {
  cor: string;
  nome: string;
  onMudar: (cor: string) => void;
}) {
  const campo = useRef<HTMLInputElement>(null);
  const mudar = useRef(onMudar);

  useEffect(() => {
    mudar.current = onMudar;
  });

  useEffect(() => {
    const elemento = campo.current;
    if (elemento === null) return;
    /** Grava a cor escolhida quando o seletor fecha. */
    function handleMudanca() {
      if (elemento !== null) mudar.current(elemento.value);
    }
    elemento.addEventListener('change', handleMudanca);
    return () => elemento.removeEventListener('change', handleMudanca);
  }, []);

  return (
    <input
      ref={campo}
      type="color"
      title="Alterar cor"
      aria-label={`Cor de ${nome}`}
      defaultValue={cor}
      className="h-[30px] w-[30px] shrink-0 cursor-pointer rounded-[6px] border-2 border-emb-borda bg-transparent p-px"
    />
  );
}

/**
 * O cartão de uma categoria.
 *
 * @param categoria - A categoria.
 * @param podeEditar - Mostra o que grava.
 * @param onNovaSubcategoria - Abre "Nova Subcategoria".
 * @returns O cartão.
 */
function CartaoDaCategoria({
  categoria,
  podeEditar,
  onNovaSubcategoria,
}: {
  categoria: Categoria;
  podeEditar: boolean;
  onNovaSubcategoria: () => void;
}) {
  const notificar = useNotificacao();
  const mudarCor = useMudarCorDaCategoria();
  const excluirCategoria = useExcluirCategoria();
  const excluirSubcategoria = useExcluirSubcategoria();
  const cor = categoria.cor ?? COR_PADRAO_DA_CATEGORIA;

  /**
   * Avisa a falha de uma gravação em vermelho.
   *
   * @param erro - O que a mutação lançou.
   */
  function avisarFalha(erro: unknown) {
    notificar(erro instanceof Error ? erro.message : 'Não foi possível gravar.', false);
  }

  /** O 🗑: pergunta e exclui (l. 6016–6021, com a exceção 7). */
  async function handleExcluirCategoria() {
    if (
      !window.confirm(
        `Excluir categoria "${categoria.nome}"?\nEssa ação não pode ser desfeita.`,
      )
    ) {
      return;
    }
    try {
      await excluirCategoria.mutateAsync(categoria.id);
      notificar('Categoria excluída');
    } catch (erro) {
      avisarFalha(erro);
    }
  }

  /**
   * O ✕ de uma subcategoria: pergunta e remove (l. 6009–6014).
   *
   * @param id - A subcategoria.
   * @param nome - O nome dela, para a pergunta.
   */
  async function handleExcluirSubcategoria(id: string, nome: string) {
    if (!window.confirm(`Remover subcategoria "${nome}"?`)) return;
    try {
      await excluirSubcategoria.mutateAsync(id);
      notificar('Subcategoria removida');
    } catch (erro) {
      avisarFalha(erro);
    }
  }

  return (
    <Cartao>
      <CabecalhoDoCartao className="flex-col items-stretch gap-2 px-3.5 py-2.5">
        <div className="flex items-center gap-2.5">
          {podeEditar ? (
            <SeletorDeCor
              key={cor}
              cor={cor}
              nome={categoria.nome}
              onMudar={(nova) => {
                mudarCor.mutateAsync({ id: categoria.id, cor: nova }).catch(avisarFalha);
              }}
            />
          ) : (
            <span
              aria-hidden="true"
              className="h-[30px] w-[30px] shrink-0 rounded-[6px] border-2 border-emb-borda"
              style={{ background: cor }}
            />
          )}
          <span className="flex-1 font-outfit text-[13px] font-light">{categoria.nome}</span>
          {categoria.sistema ? (
            <span className="shrink-0 text-[10px] whitespace-nowrap text-texto-fraco">
              sistema
            </span>
          ) : (
            podeEditar && (
              <Botao
                variante="perigo"
                tamanho="sm"
                icone
                title="Excluir categoria"
                aria-label={`Excluir categoria ${categoria.nome}`}
                className="shrink-0"
                onClick={() => void handleExcluirCategoria()}
              >
                🗑
              </Botao>
            )
          )}
        </div>
        {podeEditar && (
          <Botao
            variante="fantasma"
            tamanho="sm"
            className="self-start"
            onClick={onNovaSubcategoria}
          >
            + Subcategoria
          </Botao>
        )}
      </CabecalhoDoCartao>
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-emb-borda bg-superficie-2">
            <th className="px-3 py-[5px] text-left text-[10px] font-bold tracking-[0.5px] text-texto-fraco uppercase">
              Subcategorias
            </th>
            <th className="w-12" />
          </tr>
        </thead>
        <tbody>
          {categoria.subcategorias.length === 0 ? (
            <tr>
              <td colSpan={2} className="px-3 py-2.5 text-[12px] text-texto-fraco italic">
                Nenhuma subcategoria cadastrada
              </td>
            </tr>
          ) : (
            categoria.subcategorias.map((subcategoria) => (
              <tr key={subcategoria.id} className="border-b border-emb-borda">
                <td className="px-3 py-1.5 font-sans text-[12px] font-light">
                  {subcategoria.nome}
                </td>
                <td className="px-2.5 py-1.5 text-right">
                  {podeEditar && !subcategoria.sistema && !subcategoria.emUso && (
                    <Botao
                      variante="perigo"
                      tamanho="sm"
                      icone
                      title="Remover"
                      aria-label={`Remover ${subcategoria.nome}`}
                      onClick={() =>
                        void handleExcluirSubcategoria(subcategoria.id, subcategoria.nome)
                      }
                    >
                      ✕
                    </Botao>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </Cartao>
  );
}

/**
 * O Plano de Contas.
 *
 * @param plano - O plano, ou indefinido enquanto não chega.
 * @param falhou - Se a consulta falhou.
 * @param onTentarDeNovo - Pede o plano de novo.
 * @param podeEditar - Mostra o que grava.
 * @param onExportar - O "Exportar XLSX".
 * @returns O cabeçalho, a grade de cartões e as caixas de criar.
 */
export function PlanoDeContas({
  plano,
  falhou,
  onTentarDeNovo,
  podeEditar,
  onExportar,
}: PlanoDeContasProps) {
  const [novaCategoria, setNovaCategoria] = useState(false);
  const [categoriaDaNovaSub, setCategoriaDaNovaSub] = useState<Categoria | null>(null);
  const quantas = plano?.length ?? 0;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2.5">
        <div>
          <div className={CLASSE_DO_TITULO_DE_CADASTROS}>Plano de Contas</div>
          <div className="mt-0.5 text-[11px] text-texto-fraco">
            {plano === undefined
              ? '—'
              : `${String(quantas)} categoria${quantas !== 1 ? 's' : ''}`}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {podeEditar && (
            <BotaoDeAcao onClick={() => setNovaCategoria(true)}>
              <IconPlus size={13} aria-hidden="true" /> Nova Categoria
            </BotaoDeAcao>
          )}
          <BotaoDeAcao onClick={onExportar}>
            <IconTableExport size={13} aria-hidden="true" /> Exportar XLSX
          </BotaoDeAcao>
        </div>
      </div>

      {plano === undefined ? (
        <div className="py-2.5 text-[12px] text-texto-fraco italic">
          {falhou ? (
            <>
              Não foi possível carregar.{' '}
              <Botao variante="fantasma" tamanho="sm" onClick={onTentarDeNovo}>
                Tentar de novo
              </Botao>
            </>
          ) : (
            'Carregando…'
          )}
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-3.5">
          {plano.map((categoria) => (
            <CartaoDaCategoria
              key={categoria.id}
              categoria={categoria}
              podeEditar={podeEditar}
              onNovaSubcategoria={() => setCategoriaDaNovaSub(categoria)}
            />
          ))}
        </div>
      )}

      {novaCategoria && (
        <ModalNovaCategoria plano={plano ?? []} onFechar={() => setNovaCategoria(false)} />
      )}
      {categoriaDaNovaSub !== null && (
        <ModalNovaSubcategoria
          categoria={categoriaDaNovaSub}
          onFechar={() => setCategoriaDaNovaSub(null)}
        />
      )}
    </div>
  );
}
